import { useMutation } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { SendCodeResponse, SignupResponse, SignupRequest } from '@/api/types';

/** New pharmacy signup. Response requires email verification before login. */
export function useSignup() {
  return useMutation({
    mutationFn: (request: SignupRequest) => api.post<SignupResponse>('/api/public/signup', request),
  });
}

/** Sends a 6-digit code to the given email, for a fresh account. */
export function useSendVerification() {
  return useMutation({
    mutationFn: (email: string) => api.post<SendCodeResponse>('/api/auth/send-verification', { email }),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (request: { email: string; code?: string; token?: string }) =>
      api.post<void>('/api/auth/verify-email', request),
  });
}

/** Step 1 of the security-question reset path: fetch the stored question. */
export function useForgotPasswordQuestion() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ question: string }>('/api/auth/forgot-password/question', { email }),
  });
}

/** Step 2: answer the question and set a new password. */
export function useResetViaQuestion() {
  return useMutation({
    mutationFn: (request: { email: string; answer: string; newPassword: string }) =>
      api.post<void>('/api/auth/forgot-password/reset', request),
  });
}

/** Email-code reset path: send a reset code. */
export function useForgotPasswordEmail() {
  return useMutation({
    mutationFn: (email: string) => api.post<SendCodeResponse>('/api/auth/forgot-password/email', { email }),
  });
}

/** Email-code reset path: redeem the code for a new password. */
export function useResetViaEmail() {
  return useMutation({
    mutationFn: (request: { email: string; code?: string; token?: string; newPassword: string }) =>
      api.post<void>('/api/auth/reset-password/email', request),
  });
}

/**
 * Sets or replaces the current user's security question — the prerequisite
 * for the question-based password reset path. Requires re-entering the
 * current password, same as any other credential change.
 */
export function useSetSecurityQuestion() {
  return useMutation({
    mutationFn: (request: { question: string; answer: string; currentPassword: string }) =>
      api.put<void>('/api/auth/security-question', request),
  });
}
